/** XeAgents.ai**/
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  MessageSquare,
  ShieldAlert,
  HelpCircle,
  GitBranch,
  Calendar,
  FileText,
  Webhook,
  Phone,
  Clock,
  StopCircle,
  CheckCircle2,
  ArrowRight,
  Lightbulb,
  Workflow,
  Bot,
  PlayCircle,
  Save,
} from "lucide-react";

interface FlowBuilderHelpDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const nodeTypes = [
  {
    type: "message",
    icon: MessageSquare,
    color: "bg-blue-500",
    labelKey: "flows.nodeTypes.message",
    descKey: "flows.help.nodeTypes.messageDesc",
  },
  {
    type: "recordingConsent",
    icon: ShieldAlert,
    color: "bg-rose-500",
    labelKey: "flows.nodeTypes.recordingConsent",
    descKey: "flows.help.nodeTypes.recordingConsentDesc",
    fallbackLabel: "Recording Disclosure",
    fallbackDesc: "GDPR/Compliance: Inform the caller that the call may be recorded for training and quality monitoring. Place this immediately after the greeting.",
  },
  {
    type: "question",
    icon: HelpCircle,
    color: "bg-purple-500",
    labelKey: "flows.nodeTypes.question",
    descKey: "flows.help.nodeTypes.questionDesc",
  },
  {
    type: "condition",
    icon: GitBranch,
    color: "bg-amber-500",
    labelKey: "flows.nodeTypes.condition",
    descKey: "flows.help.nodeTypes.conditionDesc",
  },
  {
    type: "appointment",
    icon: Calendar,
    color: "bg-emerald-500",
    labelKey: "flows.nodeTypes.appointment",
    descKey: "flows.help.nodeTypes.appointmentDesc",
  },
  {
    type: "form",
    icon: FileText,
    color: "bg-cyan-500",
    labelKey: "flows.nodeTypes.form",
    descKey: "flows.help.nodeTypes.formDesc",
  },
  {
    type: "webhook",
    icon: Webhook,
    color: "bg-violet-500",
    labelKey: "flows.nodeTypes.webhook",
    descKey: "flows.help.nodeTypes.webhookDesc",
  },
  {
    type: "transfer",
    icon: Phone,
    color: "bg-pink-500",
    labelKey: "flows.nodeTypes.transfer",
    descKey: "flows.help.nodeTypes.transferDesc",
  },
  {
    type: "delay",
    icon: Clock,
    color: "bg-orange-500",
    labelKey: "flows.nodeTypes.delay",
    descKey: "flows.help.nodeTypes.delayDesc",
  },
  {
    type: "end",
    icon: StopCircle,
    color: "bg-red-500",
    labelKey: "flows.nodeTypes.end",
    descKey: "flows.help.nodeTypes.endDesc",
  },
];

export function FlowBuilderHelpDrawer({
  open,
  onOpenChange,
}: FlowBuilderHelpDrawerProps) {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Lightbulb className="h-5 w-5 text-teal-500" />
            {t("flows.help.title")}
          </SheetTitle>
          <SheetDescription>
            {t("flows.help.subtitle")}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 pb-8">
          {/* Getting Started Steps */}
          <section>
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-teal-500" />
              {t("flows.help.gettingStarted")}
            </h3>

            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">1</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <Workflow className="h-4 w-4 text-muted-foreground" />
                    {t("flows.help.step1.title")}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("flows.help.step1.description")}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">2</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    {t("flows.help.step2.title")}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("flows.help.step2.description")}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">3</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    {t("flows.help.step3.title")}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("flows.help.step3.description")}
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">4</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <Bot className="h-4 w-4 text-muted-foreground" />
                    {t("flows.help.step4.title")}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("flows.help.step4.description")}
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">5</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <PlayCircle className="h-4 w-4 text-muted-foreground" />
                    {t("flows.help.step5.title")}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("flows.help.step5.description")}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <Separator />

          {/* Node Types Reference */}
          <section>
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-teal-500" />
              {t("flows.help.nodeTypesTitle")}
            </h3>

            <div className="space-y-3">
              {nodeTypes.map((node) => {
                const Icon = node.icon;
                return (
                  <div key={node.type} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${node.color} flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm">{t(node.labelKey)}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t(node.descKey)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <Separator />

          {/* Variables & Conditions */}
          <section>
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-teal-500" />
              {t("flows.help.variablesTitle")}
            </h3>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("flows.help.variablesDescription")}
                </p>
                <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">{t("flows.help.example")}:</p>
                  <div className="text-xs font-mono space-y-1">
                    <div><span className="text-purple-600 dark:text-purple-400">Question:</span> "Would you like to be transferred?"</div>
                    <div><span className="text-purple-600 dark:text-purple-400">Variable:</span> transfer_consent</div>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">{t("flows.help.conditionOperators")}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Badge variant="outline" className="justify-center font-mono text-xs">== (equals)</Badge>
                  <Badge variant="outline" className="justify-center font-mono text-xs">!= (not equals)</Badge>
                  <Badge variant="outline" className="justify-center font-mono text-xs">&gt; (greater)</Badge>
                  <Badge variant="outline" className="justify-center font-mono text-xs">&lt; (less than)</Badge>
                  <Badge variant="outline" className="justify-center font-mono text-xs col-span-2">contains</Badge>
                </div>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2">{t("flows.help.conditionExamples")}:</p>
                <div className="text-xs font-mono space-y-1">
                  <div>transfer_consent == "yes"</div>
                  <div>age &gt; 18</div>
                  <div>response contains "help"</div>
                </div>
              </div>
            </div>
          </section>

          <Separator />

          {/* Tips */}
          <section>
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              {t("flows.help.tipsTitle")}
            </h3>

            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-1">•</span>
                {t("flows.help.tip1")}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-1">•</span>
                {t("flows.help.tip2")}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-1">•</span>
                {t("flows.help.tip3")}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-1">•</span>
                {t("flows.help.tip4")}
              </li>
            </ul>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
